<script setup lang="ts">
import heroAuthSrc from '@peanuds/ui/assets/media/hero-auth.png';
import { UiButton } from '@peanuds/ui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore, useHubspotChat } from '@peanuds/app-shell';
import AppLogo from './AppLogo.vue';

const route = useRoute();
const { openChat } = useHubspotChat();
const authStore = useAuthStore();
const { logout } = authStore;
const routeKey = computed(() => route.fullPath);
// this could be more styles in the future - if needed, this is the entry into the component
const hasExtraMainPadding = computed(() => route.meta.hasExtraMainPadding === true);
const isWorkspaceSelectionLayout = computed(
  () => route.meta.authLayoutVariant === 'workspace-selection',
);

const heroAuthUrl = computed(() => `url('${heroAuthSrc}')`);
</script>

<template>
  <div
    :class="[
      'auth-layout',
      {
        'dots-linear-gradient': !isWorkspaceSelectionLayout,
        'auth-layout--workspace-selection': isWorkspaceSelectionLayout,
      },
    ]"
  >
    <header v-if="!isWorkspaceSelectionLayout" class="auth-layout__header">
      <div class="auth-layout__logo">
        <AppLogo />
      </div>

      <div class="auth-layout__support">
        <UiButton
          class="auth-layout__support-link"
          label="Contact support"
          icon="pi pi-comments"
          icon-pos="right"
          severity="secondary"
          variant="ghost"
          @click="openChat"
        />
      </div>
    </header>

    <main
      :class="[
        'auth-layout__main',
        {
          'auth-layout__main--extra-padding': hasExtraMainPadding,
          'auth-layout__main--workspace-selection': isWorkspaceSelectionLayout,
        },
      ]"
    >
      <template v-if="isWorkspaceSelectionLayout">
        <div class="auth-layout__workspace-shell">
          <div class="auth-layout__workspace-content">
            <AppLogo class="auth-layout__workspace-logo" />
            <router-view :key="routeKey" />
          </div>

          <footer class="auth-layout__workspace-footer">
            <UiButton
              class="auth-layout__footer-link"
              label="Logout"
              severity="secondary"
              variant="ghost"
              @click="logout"
            />
          </footer>
        </div>
      </template>

      <div v-else class="auth-layout__card dots-pattern">
        <router-view :key="routeKey" />
      </div>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.auth-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  background-image:
    linear-gradient(180deg, rgba(242, 242, 247, 0.75) 25.69%, rgba(193, 197, 226, 0.75) 100%),
    v-bind(heroAuthUrl);

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &--workspace-selection {
    min-height: 100vh;
    background: $color-bg-canvas;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    font-size: 0.95rem;
  }

  &__logo {
    display: flex;
    align-items: center;
  }

  &__support-link {
    gap: 0.35rem;
    padding: 0;
    color: $color-text-primary;
    font-size: inherit;
    font-weight: $font-weight-medium;

    &:hover {
      background: transparent;
      text-decoration: underline;
    }
  }

  &__main {
    flex: 1;
    padding: 2rem 2rem 2rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    &--extra-padding {
      padding-bottom: 6rem;
    }

    &--workspace-selection {
      align-items: stretch;
      box-sizing: border-box;
      padding: clamp($space-sm, 3vh, $space-xl) $space-lg;
    }
  }

  &__workspace-shell {
    display: flex;
    width: min(100%, 600px);
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: min(6%, $space-2xl);
  }

  &__workspace-content {
    display: flex;
    width: 100%;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    gap: min(6%, $space-2xl);
  }

  &__workspace-logo {
    flex: 0 0 auto;
  }

  &__workspace-footer {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: $space-lg;
    flex-wrap: wrap;
  }

  &__footer-link {
    min-height: 36px;
    padding: 0 $space-sm;
    color: $color-text-primary;
    font-size: $text-sm;
    font-weight: $font-weight-medium;

    &:hover {
      background: transparent;
      text-decoration: underline;
    }
  }

  &__card {
    position: relative;
    background-color: $color-bg-widget;
    border-radius: 1rem;
    padding: 1rem;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    /* remove the existing ::before rules */
    &::before {
      content: none;
    }
  }
}
</style>
